import type { ReactNode } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, MoreHorizontal, Trash2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { cn } from "~/lib/utils";
import type { EvalRunListItem } from "~/services/evals";
import { CellSkeleton } from "./EvalSkeletons";
import { rowActivateProps } from "./EvalTable";
import { type TableSort, useTableSort } from "./TableSort";
import { RUN_SORT_DIRECTIONS, RunSortKey } from "./constants/sorting";
import { RunAgentAvatar } from "./primitives/RunAgentAvatar";
import { RunStatusBadge } from "./primitives/badges";
import { TagChips } from "./primitives/chips";
import { ScoreValue } from "./primitives/figures";
import { formatDuration, formatEvalTimestamp, shortModelName } from "./primitives/format";
import { parseApiTimestamp } from "./selectors/core";
import { type RunSummary, type RunTableRow, sortRunRows } from "./selectors/runs";
import type { RunAgentLabels } from "./useEvalApi";

/**
 * A run outside the measured window. Not the `—` the measured columns use for
 * "nothing there": these two say opposite things.
 */
const NotMeasured = () => (
  <span
    className="text-muted-foreground/50"
    title="Outcomes load for the most recent runs. Open this run to see its results."
  >
    ·
  </span>
);

interface RunsListTableProps {
  rows: RunTableRow[];
  agentLabels: RunAgentLabels;
  /** Whether the per-run results are still arriving. */
  resultsLoading: boolean;
  onSelectRun: (runId: string) => void;
  /** Withheld from a caller the API would refuse, so the row menu is not drawn at all. */
  onDeleteRun: ((run: EvalRunListItem) => void) | null;
}

/**
 * Runs, one row each. What a run was asked to do — agent, case selector,
 * iterations, concurrency — comes before what it came back with, so a row reads
 * as a question and then its answer. A row opens the run's drawer.
 */
export const RunsListTable = ({
  rows,
  agentLabels,
  resultsLoading,
  onSelectRun,
  onDeleteRun,
}: RunsListTableProps) => {
  const sort = useTableSort<RunSortKey>(RunSortKey.STARTED, "desc", RUN_SORT_DIRECTIONS);
  const sorted = sortRunRows(rows, sort.sortKey, sort.direction);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <SortableRunHead columnKey={RunSortKey.STARTED} label="Started" sort={sort} />
          <SortableRunHead columnKey={RunSortKey.STATUS} label="Status" sort={sort} />
          <TableHead>Agent</TableHead>
          <TableHead>Case selector</TableHead>
          <TableHead className="text-right">Iterations</TableHead>
          <TableHead className="text-right">Max concurrency</TableHead>
          <SortableRunHead columnKey={RunSortKey.CASES} label="Cases" alignRight sort={sort} />
          <SortableRunHead
            columnKey={RunSortKey.MEAN_SCORE}
            label="Mean score"
            alignRight
            sort={sort}
          />
          <SortableRunHead
            columnKey={RunSortKey.DURATION}
            label="Duration"
            alignRight
            sort={sort}
          />
          <TableHead className="w-8" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {sorted.map(({ run, summary, durationSeconds }) => {
          // The measured columns share one gate, so they state it once.
          const outcome = (render: (summary: RunSummary) => ReactNode, skeleton: string) =>
            resultsLoading ? (
              <CellSkeleton className={skeleton} />
            ) : summary == null ? (
              <NotMeasured />
            ) : (
              render(summary)
            );
          const startedAt = formatEvalTimestamp(parseApiTimestamp(run.started_at));
          return (
            <TableRow key={run.id} {...rowActivateProps(() => onSelectRun(run.id), "group")}>
              <TableCell className="text-xs font-medium tabular-nums">{startedAt}</TableCell>
              <TableCell>
                <RunStatusBadge status={run.status} />
              </TableCell>
              <TableCell className="max-w-[240px] text-xs">
                <div className="flex items-center gap-2">
                  {run.agent_id && (
                    <RunAgentAvatar agentId={run.agent_id} agentLabels={agentLabels} size={24} />
                  )}
                  <div className="min-w-0">
                    {run.agent_id ? (
                      <span
                        className={cn(
                          "block truncate font-medium",
                          agentLabels.isNamed(run.agent_id)
                            ? "text-foreground"
                            : "italic text-muted-foreground"
                        )}
                        title={run.agent_id}
                      >
                        {agentLabels.agentLabel(run.agent_id)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                    {run.model_name && (
                      <span
                        className="mt-0.5 block truncate font-mono text-[11px] text-muted-foreground"
                        title={run.model_name}
                      >
                        {shortModelName(run.model_name)}
                      </span>
                    )}
                  </div>
                </div>
              </TableCell>
              <TableCell className="max-w-[200px]">
                <TagChips tags={run.tags} emptyLabel="All cases" />
              </TableCell>
              <TableCell className="text-right text-xs tabular-nums text-muted-foreground">
                {run.run_parameters.iterations ?? "—"}
              </TableCell>
              <TableCell className="text-right text-xs tabular-nums text-muted-foreground">
                {run.run_parameters.max_concurrency ?? "—"}
              </TableCell>
              {/* Cases covered, not results recorded. */}
              <TableCell className="text-right text-xs tabular-nums">
                {outcome((it) => it.distinctCaseCount || "—", "ml-auto w-6")}
              </TableCell>
              <TableCell className="text-right text-xs">
                {outcome(
                  (it) => (
                    <ScoreValue score={it.meanScore} />
                  ),
                  "ml-auto w-12"
                )}
              </TableCell>
              <TableCell className="text-right text-xs tabular-nums text-muted-foreground">
                {formatDuration(durationSeconds)}
              </TableCell>
              <TableCell>
                {onDeleteRun && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
                        aria-label={`Actions for the run started ${startedAt}`}
                      >
                        <MoreHorizontal className="size-4" aria-hidden />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem variant="destructive" onClick={() => onDeleteRun(run)}>
                        <Trash2 className="size-4" aria-hidden />
                        Delete run
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

/**
 * A column header the whole cell sorts, with the arrow showing from the start —
 * faint while idle, solid on the active column — the way the dataset admin table
 * draws its headers.
 */
const SortableRunHead = ({
  columnKey,
  label,
  sort,
  alignRight = false,
}: {
  columnKey: RunSortKey;
  label: string;
  sort: TableSort<RunSortKey>;
  alignRight?: boolean;
}) => {
  const isActive = columnKey === sort.sortKey;
  const Icon = !isActive ? ArrowUpDown : sort.direction === "asc" ? ArrowUp : ArrowDown;

  return (
    <TableHead
      className={cn(alignRight && "text-right")}
      aria-sort={isActive ? `${sort.direction}ending` : "none"}
    >
      <button
        type="button"
        onClick={() => sort.handleSort(columnKey)}
        className={cn(
          "inline-flex cursor-pointer items-center gap-1 select-none hover:text-foreground",
          isActive && "text-foreground"
        )}
      >
        {label}
        <Icon className={cn("size-4", !isActive && "text-muted-foreground/50")} aria-hidden />
      </button>
    </TableHead>
  );
};
